import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star,
  MapPin,
  Phone,
  Globe,
  Clock,
  Navigation,
  Bookmark,
  X,
  Pencil,
  Flag,
  Share2,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  UtensilsCrossed,
  Accessibility,
} from 'lucide-react';
import type { Place, Review } from '@wingman/shared';
import { CATEGORIES } from '@wingman/shared';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../store/authStore.js';
import { useIsMobile } from '../../hooks/useMediaQuery.js';
import { getOpenStatus } from '../map/MapPopup.js';
import ReviewModal from './ReviewModal.js';
import { getConfidenceSignal, getClosingSoon } from '../../lib/signals.js';

interface PlaceDetailsProps {
  place: Place;
  onClose: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_ABBR: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
  thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

function getTodayHours(hours: Record<string, string> | undefined): string | null {
  if (!hours) return null;
  const today = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date().getDay()];
  return hours[today] ?? null;
}

function HoursAccordion({ hours }: { hours: Record<string, string> }) {
  const [open, setOpen] = useState(false);
  const todayHours = getTodayHours(hours);
  const today = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date().getDay()];

  return (
    <div style={{ width: '100%' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 0',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--text-primary)',
        }}
      >
        <Clock size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 13, textAlign: 'left' }}>
          {todayHours ?? 'See hours'}
        </span>
        {open ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />}
      </button>

      {open && (
        <div style={{ paddingLeft: 24, paddingBottom: 8 }}>
          {DAY_ORDER.filter((d) => d in hours).map((day) => (
            <div
              key={day}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '3px 0',
                fontSize: 12,
                fontWeight: day === today ? 600 : 400,
                color: day === today ? 'var(--text-primary)' : 'var(--text-muted)',
              }}
            >
              <span>{DAY_ABBR[day] ?? day}</span>
              <span>{hours[day]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Photo header ────────────────────────────────────────────────────────────

function PhotoHeader({ place }: { place: Place }) {
  const cat = CATEGORIES.find((c) => c.id === place.categoryId);
  const catColor = cat?.color || 'var(--accent)';
  const photos = place.photos ?? [];

  if (photos.length > 0) {
    return (
      <div style={{ width: '100%', height: 180, overflowX: 'auto', display: 'flex', gap: 2, flexShrink: 0 }}>
        {photos.map((photo) => (
          <img
            key={photo.id}
            src={photo.url}
            alt={place.name}
            loading="lazy"
            style={{
              height: 180,
              width: photos.length === 1 ? '100%' : 220,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ))}
      </div>
    );
  }

  // Stylish category-color hero when no photos
  return (
    <div
      style={{
        width: '100%',
        height: 120,
        flexShrink: 0,
        background: `linear-gradient(135deg, ${catColor}18 0%, var(--panel) 70%)`,
        borderBottom: `1px solid ${catColor}30`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle grid overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `linear-gradient(${catColor}08 1px, transparent 1px), linear-gradient(90deg, ${catColor}08 1px, transparent 1px)`,
        backgroundSize: '32px 32px',
      }} />
      <div style={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        background: `${catColor}20`,
        border: `2px solid ${catColor}40`,
        display: 'grid',
        placeItems: 'center',
      }}>
        <MapPin size={22} style={{ color: catColor }} />
      </div>
      <div>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', zIndex: 1, position: 'relative' }}>
          {place.name}
        </div>
        {place.categoryName && (
          <div style={{ fontSize: 12, color: catColor, zIndex: 1, position: 'relative', marginTop: 2 }}>
            {place.categoryName}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export default function PlaceDetails({ place, onClose }: PlaceDetailsProps) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportMessage, setReportMessage] = useState<string | null>(null);
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    setLoading(true);
    api.places
      .getReviews(place.id)
      .then((res) => setReviews(res.data))
      .finally(() => setLoading(false));
  }, [place.id]);

  const handleSave = async () => {
    if (!user) { navigate('/login'); return; }
    if (saved) { await api.user.unsavePlace(place.id); setSaved(false); }
    else { await api.user.savePlace(place.id); setSaved(true); }
  };

  const handleDirections = () => {
    navigate(`/directions?toLat=${place.lat}&toLng=${place.lng}&toLabel=${encodeURIComponent(place.name)}`);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/place/${place.id}`;
    if (navigator.share) {
      await navigator.share({ title: place.name, url });
    } else {
      await navigator.clipboard.writeText(url);
    }
  };

  const handleReportClosed = async () => {
    if (!user) { navigate('/login'); return; }
    setReporting(true);
    setReportMessage(null);
    try {
      await api.places.reportIssue(place.id, { kind: 'closed', note: 'Reported closed by a map user' });
      setReportMessage('Closure report submitted for verification.');
    } catch (error) {
      setReportMessage(error instanceof Error ? error.message : 'Could not submit report.');
    } finally {
      setReporting(false);
    }
  };

  const handleReviewSubmitted = () => {
    setShowReviewModal(false);
    setLoading(true);
    api.places.getReviews(place.id).then((res) => setReviews(res.data)).finally(() => setLoading(false));
  };

  const openStatus = getOpenStatus(place);
  const cat = CATEGORIES.find((c) => c.id === place.categoryId);
  const catColor = cat?.color || 'var(--accent)';

  const panel = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--panel)' }}>
      {/* Photo / hero header */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <PhotoHeader place={place} />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close details"
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'rgba(22,27,34,0.85)',
            border: '1px solid var(--border)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            backdropFilter: 'blur(8px)',
          }}
        >
          <X size={14} />
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 0 }}>

        {/* Name + rating row (if photos present, name is only here) */}
        {(place.photos?.length ?? 0) > 0 && (
          <div style={{ marginBottom: 12 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{place.name}</h2>
            {place.categoryName && (
              <div style={{ fontSize: 12, color: catColor, marginTop: 2 }}>{place.categoryName}</div>
            )}
          </div>
        )}

        {/* Rating row */}
        {place.rating !== undefined && place.rating > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--amber)', fontFamily: 'JetBrains Mono, monospace' }}>
              {place.rating.toFixed(1)}
            </span>
            <div style={{ display: 'flex', gap: 2 }}>
              {[1,2,3,4,5].map((n) => (
                <Star
                  key={n}
                  size={14}
                  style={{
                    fill: n <= Math.round(place.rating!) ? 'var(--amber)' : 'transparent',
                    color: n <= Math.round(place.rating!) ? 'var(--amber)' : 'var(--border)',
                  }}
                />
              ))}
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>({place.reviewCount ?? 0})</span>
          </div>
        )}

        {/* Open status */}
        {openStatus !== 'unknown' && (
          <div style={{ marginBottom: 12 }}>
            <span style={{
              fontSize: 12,
              fontWeight: 600,
              color: openStatus === 'open' ? 'var(--green)' : 'var(--red)',
            }}>
              {openStatus === 'open' ? '● Open now' : '● Closed'}
            </span>
            {getTodayHours(place.hours) && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 6 }}>
                · {getTodayHours(place.hours)}
              </span>
            )}
          </div>
        )}

        {/* ── Live signals strip: confidence + closing-soon, from real data ── */}
        {(() => {
          const confidence = getConfidenceSignal(place);
          const closingIn = getClosingSoon(place);
          if (!confidence && closingIn === null) return null;
          const toneColor = (t: string) =>
            t === 'green' ? 'var(--green)' : t === 'amber' ? 'var(--amber)' : 'var(--text-dim)';
          return (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12,
            }}>
              {confidence && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px',
                  border: `1px solid ${toneColor(confidence.tone)}40`,
                  background: `${toneColor(confidence.tone)}12`,
                  borderRadius: 999,
                  fontSize: 11, fontWeight: 600,
                  color: toneColor(confidence.tone),
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: toneColor(confidence.tone), flexShrink: 0,
                  }} />
                  {confidence.label}
                  {(place.reviewCount ?? 0) > 0 && (
                    <span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
                      · {place.reviewCount}
                    </span>
                  )}
                </span>
              )}
              {closingIn !== null && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px',
                  border: '1px solid rgba(210,153,34,0.4)',
                  background: 'rgba(210,153,34,0.1)',
                  borderRadius: 999,
                  fontSize: 11, fontWeight: 600,
                  color: 'var(--amber)',
                }}>
                  <Clock size={11} />
                  Closes in {closingIn}m
                </span>
              )}
            </div>
          );
        })()}

        {/* Freshness warning */}
        {place.freshnessStatus && place.freshnessStatus !== 'verified' && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '8px 12px',
            border: '1px solid rgba(210,153,34,0.3)',
            background: 'rgba(210,153,34,0.06)',
            borderRadius: 4,
            marginBottom: 12,
          }}>
            <AlertTriangle size={13} style={{ color: 'var(--amber)', flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 11, color: 'var(--amber)', lineHeight: 1.5 }}>
              {place.freshnessStatus === 'reported'
                ? 'Reported for verification'
                : `Listing freshness: ${place.freshnessStatus}`}
            </span>
          </div>
        )}

        {/* ── Google Maps–style action buttons ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 8,
          marginBottom: 20,
          marginTop: 4,
        }}>
          {[
            { icon: Navigation, label: 'Directions', onClick: handleDirections, accent: true },
            { icon: saved ? CheckCircle2 : Bookmark, label: saved ? 'Saved' : 'Save', onClick: handleSave, accent: saved },
            { icon: Pencil, label: 'Review', onClick: () => user ? setShowReviewModal(true) : navigate('/login'), accent: false },
            { icon: Share2, label: 'Share', onClick: handleShare, accent: false },
          ].map(({ icon: Icon, label, onClick, accent }) => (
            <button
              key={label}
              type="button"
              onClick={onClick}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                padding: '10px 4px',
                border: `1px solid ${accent ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 8,
                background: accent ? 'var(--accent-dim)' : 'transparent',
                color: accent ? 'var(--accent)' : 'var(--text-muted)',
                cursor: 'pointer',
                fontSize: 10,
                fontWeight: 600,
                transition: 'border-color 120ms, background 120ms, color 120ms',
              }}
              onMouseEnter={(e) => {
                if (!accent) {
                  e.currentTarget.style.borderColor = 'var(--accent)';
                  e.currentTarget.style.color = 'var(--accent)';
                }
              }}
              onMouseLeave={(e) => {
                if (!accent) {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.color = 'var(--text-muted)';
                }
              }}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>

        {/* ── Info rows ── */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', flexDirection: 'column' }}>
          {place.address && (
            <div style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(48,54,61,0.5)' }}>
              <MapPin size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>{place.address}</span>
            </div>
          )}
          {place.phone && (
            <div style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(48,54,61,0.5)' }}>
              <Phone size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }} />
              <a href={`tel:${place.phone}`} style={{ fontSize: 13, color: 'var(--accent)', textDecoration: 'none' }}>{place.phone}</a>
            </div>
          )}
          {place.website && (
            <div style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(48,54,61,0.5)' }}>
              <Globe size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }} />
              <a href={place.website} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 13, color: 'var(--accent)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {place.website.replace(/^https?:\/\//, '')}
              </a>
            </div>
          )}
          {place.hours && Object.keys(place.hours).length > 0 && (
            <div style={{ borderBottom: '1px solid rgba(48,54,61,0.5)' }}>
              {place.hours.raw ? (
                <div style={{ display: 'flex', gap: 10, padding: '10px 0' }}>
                  <Clock size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>{place.hours.raw}</span>
                </div>
              ) : (
                <HoursAccordion hours={place.hours} />
              )}
            </div>
          )}
          {place.cuisine && (
            <div style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(48,54,61,0.5)' }}>
              <UtensilsCrossed size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                {place.cuisine.split(';').map((c) => c.trim()).join(' · ')}
              </span>
            </div>
          )}
          {place.wheelchair && (
            <div style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(48,54,61,0.5)' }}>
              <Accessibility size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 13, color: place.wheelchair === 'yes' ? 'var(--green)' : 'var(--text-primary)', lineHeight: 1.5 }}>
                {place.wheelchair === 'yes' ? 'Wheelchair accessible' : place.wheelchair === 'no' ? 'Not wheelchair accessible' : `Wheelchair: ${place.wheelchair}`}
              </span>
            </div>
          )}
        </div>

        {/* ── Reviews ── */}
        <div style={{ marginTop: 20 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 12,
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              Reviews
            </span>
            <button
              type="button"
              onClick={() => user ? setShowReviewModal(true) : navigate('/login')}
              style={{
                fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none',
                cursor: 'pointer', fontWeight: 600,
              }}
            >
              + Write a review
            </button>
          </div>

          {loading ? (
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading reviews…</p>
          ) : reviews.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No reviews yet. Be the first!</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {reviews.map((review) => (
                <div key={review.id} style={{ paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: 'var(--accent-dim)', border: '1px solid rgba(88,166,255,0.3)',
                      display: 'grid', placeItems: 'center',
                      color: 'var(--accent)', fontSize: 13, fontWeight: 700, flexShrink: 0,
                    }}>
                      {review.userName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{review.userName}</div>
                      <div style={{ display: 'flex', gap: 2, marginTop: 2 }}>
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} size={10} style={{
                            fill: n <= review.rating ? 'var(--amber)' : 'transparent',
                            color: n <= review.rating ? 'var(--amber)' : 'var(--border)',
                          }} />
                        ))}
                      </div>
                    </div>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-dim)' }}>
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {review.text && (
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>{review.text}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Report */}
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <button
            type="button"
            onClick={handleReportClosed}
            disabled={reporting}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 11, color: 'var(--text-dim)', background: 'none',
              border: 'none', cursor: 'pointer', padding: 0,
            }}
          >
            <Flag size={12} />
            {reporting ? 'Submitting…' : 'Report this place'}
          </button>
          {reportMessage && (
            <p style={{ fontSize: 11, color: 'var(--green)', marginTop: 6 }}>{reportMessage}</p>
          )}
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div
          className="bottom-sheet bottom-sheet-expanded z-30"
          style={{ maxHeight: '75vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
          <div className="bottom-sheet-handle" />
          {panel}
        </div>
        {showReviewModal && (
          <ReviewModal placeId={place.id} placeName={place.name}
            onClose={() => setShowReviewModal(false)} onSubmitted={handleReviewSubmitted} />
        )}
      </>
    );
  }

  return (
    <>
      {/* Left-side panel — Google Maps standard */}
      <div
        style={{
          position: 'absolute',
          top: 'var(--topbar-h)',
          left: 0,
          width: 380,
          height: 'calc(100% - var(--topbar-h))',
          zIndex: 20,
          boxShadow: '4px 0 24px rgba(0,0,0,0.5)',
          overflowY: 'auto',
        }}
      >
        {panel}
      </div>
      {showReviewModal && (
        <ReviewModal placeId={place.id} placeName={place.name}
          onClose={() => setShowReviewModal(false)} onSubmitted={handleReviewSubmitted} />
      )}
    </>
  );
}
